import { Injectable } from '@angular/core';
import { BehaviorSubject, forkJoin, Observable, of, Subject } from 'rxjs';
import { catchError, concatMap, filter, map, shareReplay, skip, takeUntil, tap } from 'rxjs/operators';
import { AppService, ProductTranslations } from './app.service';
import { ProductCategoryGroupMap, ProductsCategoryService } from './products-category.service';
import { Quote, QuoteService } from './quote.service';
import { environment } from '../../../environments/environment';
import { HttpClient } from '@angular/common/http';
import { EnvironmentDiffService } from './environment-diff.service';
import { isEqual } from 'lodash';
import { OfferService } from './offer.service';
import { OfferItem, OfferPrintOptions } from '../models/offer.model';
import { isGroupWithHintText } from '../utils/offer.util';
import { BomItem } from '../components/questionnaire/configit-quest-adapter/configit-types.interface';

export interface ProductGroup {
    name: string;
    translationKey: string;
    order?: number;
    products: Product[];
    printOptions?: OfferPrintOptions; // only for sample offers
}

export interface ProductHeadline {
    groupNameTranslation: string;
    children: string[];
}

export interface Product {
    order?: number;
    material: string;
    shortText?: string;
    quantity: number;
    category?: string;
    translation?: ProductTranslations;
    materialGroup?: string;
    canBeOptional?: boolean;
    canBeDeleted?: boolean;
    optional?: boolean;
    alternatives?: Product[]; // only for sample offers
    alternativeFor?: string; // only for sample offers
    printOptions?: OfferPrintOptions; // only for sample offers
    selected: boolean;
    productReferences?: ProductReferences;
}

export interface PriceSummary {
    totalGross: number;
    discount: number;
    totalPrice: number;
    netPrice: number;
    vat: number;
    sum: number;
    voucherValue: number;
}

export interface AssetConversion {
    conversionId: string;
    url: string;
    resolution: string;
    format: string;
}

export interface Asset {
    _id: string;
    type: string;
    name: string;
    assetConversions: AssetConversion[];
}

export interface AssetReference {
    referenceTypeId: string;
    assets: Asset[];
}

export interface TechnicalAttribute {
    attributeId: string;
    values: Value[];
    attributeGroup: string;
    dataType: string;
    decimalPlaces: number;
    description: string;
}

export interface Value {
    value: number;
    valueId: string;
    unit: string;
    unitId: string;
}

export interface GeneralData {
    materialPriceGroup: Value;
}

export interface PimPrice {
    price: {
        value: number;
        unit: string;
        unitId: string; // e.g. 'iso4217.unit.EUR'
    };
}

export interface PimProduct {
    _id: string;
    name: string;
    type: string;
    description: string;
    assetReferences: AssetReference[];
    technicalAttributes: TechnicalAttribute[];
    generalData: GeneralData;
    price: PimPrice;
    productReferences: ProductReferences;
}

export interface PimProductInfo {
    name: string;
    description: string;
    categoryName: string;
    materialGroup: string;
    locale: string;
    currency: string;
    technicalAttributes: TechnicalAttribute[];
    productReferences: ProductReferences;
}

export interface ProductReferences {
    packageContents: PackageContent[];
}

export interface PackageContent {
    _id: string;
    name: string;
    description: string;
    referenceMetadata: PackageContentMetadata;
    texts: PackageContentTexts;
}

export interface PackageContentMetadata {
    amount: number;
    posNo: string;
}

export interface PackageContentTexts {
    catalogueTextShort: string;
    offeringText: string;
    offeringTextLong: string;
}

interface BomItemWithProductInfo extends BomItem {
    info: PimProductInfo;
}

interface OfferItemWithProductInfo extends OfferItem {
    info: PimProductInfo;
}

const emptyProductInfo = <PimProductInfo>{
    categoryName: '',
    materialGroup: '',
    description: '',
    locale: '',
};

@Injectable({
    providedIn: 'root',
})
export class ProductsService {
    private unsubscribe$: Subject<void> = new Subject<void>();

    public hasErrors$: BehaviorSubject<{ value: boolean }> = new BehaviorSubject<{ value: boolean }>({ value: false });

    constructor(
        private http: HttpClient,
        private quoteService: QuoteService,
        private appService: AppService,
        private productCategory: ProductsCategoryService,
        private environmentDiffService: EnvironmentDiffService,
        private offerService: OfferService
    ) {
        this.subscribeToLanguageChanged$();
        this.materialCache = new Map<string, PimProductInfo>();
        this.materialRequests = new Map<string, Observable<string>>();
        this.productImageCache = new Map<string, string | undefined>();
        this.optionalMaterials = new Map<string, boolean>();

        this.quoteService
            .getCurrentQuoteObservable()
            .pipe(
                skip(1),
                filter((v) => v === undefined || v.isNew)
            )
            .subscribe(() => {
                this.materialCache.clear();
                this.materialRequests.clear();
            });
    }
    private materialCache: Map<string, PimProductInfo>;
    private materialRequests: Map<string, Observable<string>>;
    private productImageCache: Map<string, string | undefined>;
    private optionalMaterials: Map<string, boolean>;

    private priceSummaryCache: PriceSummary;

    public productCategoriesGroupNamesMap: ProductCategoryGroupMap = {};

    public getProductImage(material: string) {
        if (this.materialRequests.has(material)) {
            return this.materialRequests.get(material);
        }
        if (this.productImageCache.has(material)) {
            const img = this.productImageCache.get(material);
            return of(img ?? '../../../../assets/images/empty_image.png');
        }
        this.materialRequests.set(material, this.fetchProductImage(material));
        return this.materialRequests.get(material);
    }

    public addPriceSummaryToCache(
        totalGross: number,
        discount: number,
        totalPrice: number,
        netPrice: number,
        vat: number,
        sum: number,
        voucherValue: number
    ) {
        if (!this.priceSummaryCache) {
            this.priceSummaryCache = {
                totalGross: 0,
                discount: 0,
                totalPrice: 0,
                netPrice: 0,
                vat: 0,
                sum: 0,
                voucherValue: 0,
            };
        }
        this.priceSummaryCache.totalGross = totalGross;
        this.priceSummaryCache.discount = discount;
        this.priceSummaryCache.totalPrice = totalPrice;
        this.priceSummaryCache.netPrice = netPrice;
        this.priceSummaryCache.vat = vat;
        this.priceSummaryCache.sum = sum;
        this.priceSummaryCache.voucherValue = voucherValue;
    }

    public clearPriceSummaryCache() {
        this.priceSummaryCache = undefined;
        this.materialCache.clear();
        this.materialRequests.clear();
    }

    public getProducts(materials: BomItem[], quote: Quote, checkOptional = false): Observable<Product[]> {
        const materialNumbers = materials.map((m) => m.material);
        return forkJoin([
            this.getMaterialInfosForBomItems(materials, quote),
            checkOptional ? this.getOptionalMaterials(materialNumbers) : of(new Map<string, boolean>()),
        ]).pipe(
            map(([materialInfos, optionals]) =>
                materialInfos.map(({ material, componentQuantity: quantity, info }) => {
                    const { categoryName, materialGroup, description, locale } = info;

                    const translation = {
                        [locale]: description,
                    };

                    const canBeOptional = optionals.get(material);
                    const optionalCategories = this.environmentDiffService.getOptionalCategoriesFromCache();
                    const defaultOptional = canBeOptional && optionalCategories.includes(categoryName);

                    return <Product>{
                        material,
                        category: categoryName,
                        materialGroup,
                        translation,
                        quantity,
                        canBeOptional,
                        canBeDeleted: true,
                        optional: defaultOptional,
                        selected: true,
                    };
                })
            )
        );
    }

    public getMaterialInfosForBomItems(
        items: BomItem[],
        quote: Quote,
        hasImage = true
    ): Observable<BomItemWithProductInfo[]> {
        const materials = items.map((i) => i.material);
        return this.getMaterialInfos(
            materials,
            ['AT_SAP_V_KATEGORIE_QA'],
            ['generalData', 'attributes', 'price', 'references'],
            hasImage
        ).pipe(
            tap((infos) => this.updateCurrencyInQuote(quote, infos)),
            map((infos) =>
                items.map((m) => ({
                    ...m,
                    info: m.material ? infos.find((mi) => mi.name === m.material) || emptyProductInfo : undefined,
                }))
            )
        );
    }

    public getMaterialInfos(
        materials: string[],
        attributes: string[],
        dataGroups: string[],
        hasImage = true,
        cache = true
    ): Observable<PimProductInfo[]> {
        const cached: PimProductInfo[] = [];
        let materialIds: string[] = materials;
        if (cache) {
            // collect all cached materials
            materials.forEach((material) => {
                if (this.materialCache.has(material)) {
                    cached.push(this.materialCache.get(material));
                } else if (hasImage) {
                    this.fetchProductImage(material).subscribe(); // just fetch and cache, no need to care about result here
                }
            });
            if (
                isEqual(
                    materials,
                    cached.map((c) => c.name)
                )
            ) {
                return of(cached);
            }
            // fetch only material infos that are not in cache
            materialIds = materials.filter((name) => !cached.some((m) => m.name === name));
        }
        this.hasErrors$.next({ value: false });
        const url = `${environment.http.pim.baseUrl}products/${this.appService.getLanguageOnly()}/${this.appService
            .getLocation()
            .toLowerCase()}`;
        const strict = environment.http.pim.strict;
        return this.http
            .post<PimProduct[]>(
                url,
                { products: cache ? materialIds : materials, attributes, references: { packageContents: true } },
                { params: { dataGroups, strict } }
            )
            .pipe(
                map((products) => products.map((p) => this.getPimProductInfo(p))),
                tap((products) => {
                    if (!cache) {
                        return;
                    }
                    products.forEach((product) => this.materialCache.set(product.name, product));
                }),
                map((products) => (cache ? [...cached, ...products] : products)), // combine cached and retrieved data
                catchError((err) => {
                    console.log(err);
                    this.hasErrors$.next({ value: true });
                    return of([]);
                })
            );
    }

    public getProductsFromSampleOffer(offerId: string, quote: Quote): Observable<ProductGroup[]> {
        return this.offerService.getOffer(offerId).pipe(
            map((offer) => offer.items),
            concatMap((items) => {
                const allItems = items.reduce<OfferItem[]>(
                    (prev, curr) => [...prev, ...(<OfferItem[]>curr.alternatives), curr],
                    []
                );
                const materials = allItems
                    .filter((m) => !!m.material)
                    .map((i) => ({ material: i.material, itemId: `${i.itemNumber}`, componentQuantity: i.quantity }));
                const materialNumbers = materials.map((m) => m.material);

                return forkJoin([
                    this.getMaterialInfosForBomItems(materials, quote).pipe(
                        map((infos) => {
                            const extend: (item: OfferItem) => OfferItemWithProductInfo = (item: OfferItem) => ({
                                ...item,
                                info: infos.find((info) => info.material === item.material)?.info,
                                alternatives: item.alternatives.map(extend),
                            });
                            return items.map(extend);
                        })
                    ),
                    this.getOptionalMaterials(materialNumbers),
                ]);
            }),
            map(([items, optionals]) => {
                let index = 1;
                const otherGroup: ProductGroup = this.createOtherProductGroup();
                let currentGroup: ProductGroup = otherGroup;
                const toProduct = (item: OfferItemWithProductInfo) => {
                    const { categoryName, materialGroup, description, locale, productReferences } = item.info;
                    const alternatives: Product[] = item.alternatives.map(toProduct).map((alt) => {
                        const canBeOptional = optionals.get(alt.material) && alt.selected;
                        return {
                            ...alt,
                            alternativeFor: item.material,
                            canBeOptional,
                            canBeDeleted: canBeOptional,
                            optional: canBeOptional && alt.optional,
                            selected: false,
                        };
                    });
                    const canBeOptional = optionals.get(item.material);
                    return <Product>{
                        material: item.material,
                        quantity: item.quantity,
                        category: categoryName,
                        optional: canBeOptional && item.optional,
                        selected: true,
                        materialGroup,
                        translation: { [locale]: description },
                        canBeOptional,
                        canBeDeleted: canBeOptional,
                        printOptions: item.printOptions,
                        alternatives,
                        productReferences,
                    };
                };
                const productGroups = items.reduce((prev: ProductGroup[], curr) => {
                    if (curr.material) {
                        // insert product in current group
                        const product = toProduct(curr);
                        currentGroup.products.push(product, ...product.alternatives);
                        return prev;
                    } else {
                        // create new product group
                        const group = {
                            name: curr.shortText,
                            order: index++,
                            translationKey: curr.shortText,
                            products: [],
                            printOptions: curr.printOptions,
                        };

                        // do not insert products in hint groups
                        if (!isGroupWithHintText(group)) {
                            currentGroup = group;
                        }

                        return [...prev, group];
                    }
                }, <ProductGroup[]>[]);
                otherGroup.products = this.sortOtherProducts(otherGroup.products);
                return [...productGroups, otherGroup];
            })
        );
    }

    public createOtherProductGroup(products: Product[] = []): ProductGroup {
        return {
            name: 'otherGroup',
            translationKey: 'COMMON.CATEGORIES.OTHER_PRODUCT_GROUP',
            products: this.sortOtherProducts(products),
        };
    }
    public sortOtherProducts(products: Product[]): Product[] {
        return products.sort((a: Product, b: Product) => {
            const textA = this.chooseTranslation(a.translation).toUpperCase();
            const textB = this.chooseTranslation(b.translation).toUpperCase();
            return textA.localeCompare(textB);
        });
    }

    private updateCurrencyInQuote(quote: Quote, infos: PimProductInfo[]) {
        const currency = infos.map((i) => i.currency).filter(Boolean)[0];
        this.quoteService.updateCurrency(currency, quote);
    }

    private getPimProductInfo(product: PimProduct): PimProductInfo {
        const categoryName = product.technicalAttributes?.find(
            (attribute) => attribute.attributeId === 'AT_SAP_V_KATEGORIE_QA'
        )?.values[0].valueId;
        const materialGroup = product.generalData?.materialPriceGroup?.valueId;
        const locale = `${this.appService.getLanguageOnly()}-${this.appService.getLocation().toUpperCase()}`;
        const currency = product.price?.price?.unitId?.split('.')?.pop() || '';

        return <PimProductInfo>{
            name: product.name,
            description: product.description,
            categoryName,
            materialGroup,
            locale,
            currency,
            technicalAttributes: product.technicalAttributes,
            productReferences: product.productReferences,
        };
    }

    public fetchProductImage(material: string): Observable<string | undefined> {
        const url = `${environment.http.pim.baseUrl}products/${this.appService.getLanguageOnly()}/${this.appService
            .getLocation()
            .toLowerCase()}/assets`;
        const conversionId = 'AT_AssetPush_Conversion_URL_PNG_210';

        return this.http
            .post<PimProduct[]>(url, {
                products: [material],
            })
            .pipe(
                catchError(() => of(undefined)),
                map((response: PimProduct[] | undefined) => {
                    return response?.[0]?.assetReferences
                        ?.map((assetRef: AssetReference) =>
                            assetRef.assets?.map((asset: Asset) =>
                                asset.assetConversions?.find(
                                    (conv: AssetConversion) => conv.conversionId === conversionId
                                )
                            )
                        )
                        .flat()
                        .find((conversion) => conversion?.url)?.url;
                }),
                tap((imgUrl) => {
                    this.productImageCache.set(material, imgUrl);
                    this.materialRequests.delete(material);
                }),
                shareReplay()
            );
    }

    public chooseTranslation(productTranslations?: ProductTranslations) {
        return this.appService.chooseTranslation(productTranslations);
    }

    public getOptionalMaterials(materials: string[]): Observable<Map<string, boolean>> {
        const notYetFetched = materials.filter((m) => !this.optionalMaterials.has(m));
        return (notYetFetched.length ? this.fetchOptionalMaterials(notYetFetched) : of([])).pipe(
            map(() => this.optionalMaterials)
        );
    }

    private fetchOptionalMaterials(materials: string[]): Observable<string[]> {
        return this.http
            .post<string[]>(
                `${environment.http.baseUrl}material/optional`,
                { materials },
                { params: { salesOrg: this.appService.getSalesOrg() } }
            )
            .pipe(
                catchError(() => of([])),
                tap((optionals) => {
                    materials.forEach((material) => {
                        this.optionalMaterials.set(material, optionals.includes(material));
                    });
                })
            );
    }

    private subscribeToLanguageChanged$(): void {
        this.appService.salesOrg$.pipe(takeUntil(this.unsubscribe$), filter(Boolean)).subscribe((salesOrg: string) => {
            this.productCategory.fetchProductCategoryGroupNameMap(salesOrg).subscribe((res) => {
                this.productCategoriesGroupNamesMap = res;
            });
        });
    }
}
